import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Calendar as CalendarIcon, List, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { AppointmentTable } from "@/components/appointments/AppointmentTable";
import { AcceptRequestDialog } from "@/components/appointments/AcceptRequestDialog";
import { FollowUpTasks } from "@/components/appointments/FollowUpTasks";
import { PointOfContactField } from "@/components/appointments/PointOfContactField";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const localizer = momentLocalizer(moment);
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const mins = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + (m || 0); };
const spoken = (t) => { const [h, m] = t.split(":").map(Number); return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

export const outsideHoursWarning = (scheduling, f) => {
  if (!scheduling || !f.date || !f.time) return null;
  const type = scheduling.types.find((t) => t.key === f.appointment_type) || scheduling.types[0];
  if (!type) return null;
  const [y, mo, d] = f.date.split("-").map(Number);
  const weekday = (new Date(y, mo - 1, d).getDay() + 6) % 7;
  if (!type.days.includes(weekday)) return `${type.label} appointments are not normally booked on ${DAY_NAMES[weekday]}. Bookable: ${type.days.map((x) => DAY_NAMES[x]).join(", ")}.`;
  if (mins(f.time) < mins(type.start) || mins(f.time) + (f.duration || 0) > mins(type.end)) return `${type.label} hours are ${spoken(type.start)}–${spoken(type.end)}; this slot falls outside them.`;
  return null;
};

export default function Appointments() {
  const { can } = usePermissions();
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    customer_id: "",
    technician_id: "",
    title: "",
    date: new Date().toISOString().split('T')[0],
    time: "09:00",
    duration: 60,
    status: "scheduled",
    appointment_type: "onsite",
    notes: "",
    contact_name: null,
    contact_phone: null,
    contact_email: null
  });

  useEffect(() => {
    fetchAppointments();
    fetchCustomers();
    fetchTechnicians();
    axios.get(`${API}/scheduling-settings`).then((r) => setScheduling(r.data)).catch(() => {});
  }, []);

  const editId = searchParams.get("edit");
  useEffect(() => {
    if (!editId || loading) return;
    const target = appointments.find((a) => a.id === editId);
    if (target) handleEdit(target); else toast.error("Appointment not found");
    setSearchParams({}, { replace: true });
  }, [editId, loading, appointments]);

  const hoursWarning = outsideHoursWarning(scheduling, formData);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (error) {
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (error) {
      toast.error("Failed to fetch technicians");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const poc = ["contact_name", "contact_phone", "contact_email"].map((k) => (formData[k] || "").trim());
    const payload = { ...formData, technician_id: formData.technician_id || null, contact_name: poc.some(Boolean) ? poc[0] : null, contact_phone: poc.some(Boolean) ? poc[1] : null, contact_email: poc.some(Boolean) ? poc[2] : null };
    try {
      if (editingAppointment) {
        await axios.put(`${API}/appointments/${editingAppointment.id}`, payload);
        toast.success("Appointment updated successfully");
      } else {
        await axios.post(`${API}/appointments`, payload);
        toast.success("Appointment created successfully");
      }
      setOpen(false);
      resetForm();
      fetchAppointments();
    } catch (error) {
      toast.error(editingAppointment ? "Failed to update appointment" : "Failed to create appointment");
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      customer_id: appointment.customer_id,
      technician_id: appointment.technician_id || "",
      title: appointment.title,
      date: appointment.date,
      time: appointment.time,
      duration: appointment.duration,
      status: appointment.status,
      appointment_type: appointment.appointment_type || "onsite",
      notes: appointment.notes || "",
      contact_name: appointment.contact_name || null,
      contact_phone: appointment.contact_phone || null,
      contact_email: appointment.contact_email || null
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("Appointment deleted successfully");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      technician_id: "",
      title: "",
      date: new Date().toISOString().split('T')[0],
      time: "09:00",
      duration: 60,
      status: "scheduled",
      appointment_type: "onsite",
      notes: "",
      contact_name: null,
      contact_phone: null,
      contact_email: null
    });
    setEditingAppointment(null);
  };

  // Convert appointments to calendar events
  const isOpen = (apt) => !["completed", "cancelled", "canceled"].includes(apt.status);
  const calendarEvents = appointments.map(apt => {
    const [hours, minutes] = apt.time.split(':').map(Number);
    // Parse date correctly to avoid timezone issues
    const dateParts = apt.date.split('-');
    const start = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], hours, minutes, 0);
    const end = new Date(start.getTime() + apt.duration * 60000);
    const prefix = isOpen(apt) && apt.reschedule_requested_at ? "↻ " : isOpen(apt) && apt.confirmed_via ? "✓ " : "";

    return {
      id: apt.id,
      title: `${prefix}${apt.title} - ${apt.customer_name}`,
      start,
      end,
      resource: apt,
    };
  });

  const eventStyleGetter = (event) => {
    const statusColors = {
      scheduled: { backgroundColor: '#3b82f6', borderColor: '#2563eb' },
      confirmed: { backgroundColor: '#059669', borderColor: '#047857' },
      completed: { backgroundColor: '#10b981', borderColor: '#059669' },
      cancelled: { backgroundColor: '#ef4444', borderColor: '#dc2626' },
    };
    const apt = event.resource;
    const colors = isOpen(apt) && apt.reschedule_requested_at ? { backgroundColor: '#d97706', borderColor: '#b45309' } : statusColors[apt.status] || statusColors.scheduled;
    return {
      style: {
        ...colors,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: '0',
        display: 'block',
        fontSize: '13px',
        padding: '4px 8px'
      },
      className: isOpen(apt) && apt.confirmed_via ? "rbc-event-confirmed-sms" : undefined,
    };
  };

  const handleSelectEvent = (event) => {
    handleEdit(event.resource);
  };

  return (
    <div data-testid="appointments-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Appointments</h1>
          <p className="text-slate-600">Schedule and manage customer visits</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg ${can("appointments.create") ? "" : "hidden"}`} data-testid="add-appointment-button">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingAppointment ? "Edit Appointment" : "Schedule New Appointment"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="customer">Customer *</Label>
                <CustomerCombobox
                  customers={customers}
                  value={formData.customer_id}
                  onChange={(value) => setFormData({ ...formData, customer_id: value })}
                  onCreated={(c) => setCustomers((prev) => [...prev, c])}
                  allowCreate
                  testId="customer-select"
                />
              </div>
              <PointOfContactField customer={customers.find((c) => c.id === formData.customer_id)} value={formData} onChange={(v) => setFormData({ ...formData, ...v })} />
              <div>
                <Label htmlFor="technician">Technician</Label>
                <Select value={formData.technician_id || "unassigned"} onValueChange={(value) => setFormData({ ...formData, technician_id: value === "unassigned" ? "" : value })}>
                  <SelectTrigger data-testid="technician-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned" data-testid="technician-option-unassigned">Unassigned — notify default contact</SelectItem>
                    {technicians.map((technician) => (
                      <SelectItem key={technician.id} value={technician.id}>{technician.name} - {technician.specialty}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.technician_id && <p className="text-xs text-amber-700 mt-1" data-testid="unassigned-hint">No technician selected — the default appointment contact will be emailed to assign one.</p>}
              </div>
              <div>
                <Label htmlFor="appointment_type">Appointment type *</Label>
                <Select value={formData.appointment_type} onValueChange={(value) => { const t = scheduling?.types.find((x) => x.key === value); setFormData({ ...formData, appointment_type: value, duration: t ? t.duration : formData.duration }); }}>
                  <SelectTrigger data-testid="appointment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(scheduling?.types || [{ key: "onsite", label: "On-site visit", duration: 60 }]).map((t) => (
                      <SelectItem key={t.key} value={t.key} data-testid={`appointment-type-option-${t.key}`}>{t.label} · {t.duration} min · {t.start ? `${spoken(t.start)}–${spoken(t.end)}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  data-testid="title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="e.g., Consultation, Site Visit"
                />
              </div>
              <div>
                <Label htmlFor="notes">Reason / description for the technician</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1"
                  rows={3}
                  placeholder="What the customer reported, equipment involved, access instructions, gate code…"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    data-testid="time-input"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (minutes) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    data-testid="duration-input"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                    required
                    min="15"
                    step="15"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })} required>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hoursWarning && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="outside-hours-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{hoursWarning} You can still save it.</span>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-appointment-button">
                  {editingAppointment ? "Update" : "Schedule"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <FollowUpTasks refreshKey={appointments} onOpenAppointment={(id) => { const t = appointments.find((a) => a.id === id); if (t) handleEdit(t); else toast.error("Appointment not found"); }} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : appointments.length === 0 ? (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarIcon className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No appointments scheduled</h3>
            <p className="text-slate-500">Schedule your first appointment to get started</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="list" className="space-y-6">
          <TabsList>
            <TabsTrigger value="list">
              <List className="w-4 h-4 mr-2" />
              List View
            </TabsTrigger>
            <TabsTrigger value="calendar">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar View
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            <AppointmentTable
              appointments={appointments}
              customers={customers}
              technicians={technicians}
              onDelete={can("appointments.delete") ? handleDelete : null}
              onEdit={can("appointments.edit") ? handleEdit : null}
              onAccept={can("appointments.edit") && can("appointments.assign") ? setAccepting : null}
            />
            {accepting && <AcceptRequestDialog appointment={accepting} technicians={technicians} onClose={() => setAccepting(null)} onDone={() => { setAccepting(null); fetchAppointments(); }} />}
          </TabsContent>

          <TabsContent value="calendar">
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-3 text-xs text-slate-600 mb-3" data-testid="calendar-legend">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#3b82f6]" /> Scheduled</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#059669]" /> ✓ Confirmed by text</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#d97706]" /> ↻ Reschedule requested</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#10b981]" /> Completed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#ef4444]" /> Cancelled</span>
                </div>
                <div style={{ height: '700px' }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    views={['month', 'week', 'day']}
                    defaultView="month"
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleSelectEvent}
                    popup
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
